'use client';

import React, { useContext, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { useRouter, useSearchParams } from 'next/navigation';
import styles from './searchResultsContainer.module.scss';
import retrieveSearchResults from '@/actions/retrieve-search-results';
import { navigateToAnchor } from '@/utils/helpCenterUtils';
import URLGenerator from '@/utils/URLGenerator';
import { useAccessibilityContext } from '../AccessibilityProvider';
import { useTrack } from '@chewy/use-analytics';
import ContactUsButtonPanel from './ContactUsButtonPanel';
import OHCContext from '@/providers/OHCContext';
import Lottie from 'lottie-react';
import animation from '@/components/animation/dogLightBlue.json';

const SearchResultsContainer = () => {
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('searchQuery') || '';
  const router = useRouter();
  const { announce, announceLoading } = useAccessibilityContext();
  const { isResultsLoading, setIsResultsLoading, searchResults, setSearchResults, isNavError } = useContext(OHCContext);
  const track = useTrack();

  const generateSectionUrl = (sectionId: string) => {
    const urlGenerator = new URLGenerator('/csrb/nma/customer-care');
    urlGenerator.addSection({ value: sectionId });
    return urlGenerator.generateURL();
  };

  const doOnTopicClick = (section: any) => {
    navigateToAnchor(section?.id, router);
    track(`Search Results Clicked`, {
      id: 'search-results-clicked-id',
      parentId: 'search-results-id',
      level: 'help-center',
      type: 'section',
      position: 1,
      event_name: 'Link Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-search-results',
      eventLabel: 'help-center-search-results-click',
      name: 'Search Results Link Clicked',
      searchValue: searchQuery,
      sectionId: section?.id,
      sectionTitle: section?.title || 'No title available.'
    });
  };

  useEffect(() => {
    if (!isResultsLoading) {
      if (isEmpty(searchResults)) {
        track(`Search Results No Results Found`, {
          id: 'search-results-no-results-found-id',
          parentId: 'search-results-id',
          level: 'help-center',
          type: 'section',
          position: 1,
          event_name: 'Widget Viewed',
          eventAction: 'impression',
          eventCategory: 'help-center-search-no-results',
          eventLabel: 'help-center-search-no-results-click',
          name: 'Search Results No Results Found',
          searchValue: searchQuery
        });
      } else {
        track(`Search Results Viewed`, {
          id: 'search-results-viewed-id',
          parentId: 'search-results-id',
          level: 'help-center',
          type: 'section',
          position: 1,
          event_name: 'Widget Viewed',
          eventAction: 'impression',
          eventCategory: 'help-center-search-results',
          eventLabel: 'help-center-search-results-view',
          name: 'Search Results Viewed',
          searchValue: searchQuery,
          searchResults: searchResults
        });
      }
    }
  }, [searchResults, searchQuery, isResultsLoading, track]);

  useEffect(() => {
    const getSuggestions = async (searchValue: string) => {
      setIsResultsLoading(true);
      announceLoading(true, `Searching for “${searchValue}”…`);

      try {
        const data = await retrieveSearchResults({ searchValue });
        const results = data?.results || [];
        setSearchResults(results);

        const resultCount = results.length;
        const message = resultCount > 0 ? `Found ${resultCount} result${resultCount === 1 ? '' : 's'} for “${searchValue}”` : `No results found for “${searchValue}”`;

        announce(message);
        announceLoading(false, '', message);
      } catch (error) {
        console.error(error);
        announce('An error occurred while searching. Please try again.', 'assertive');
        announceLoading(false, '', 'Search failed');
        setSearchResults([]);
      } finally {
        setIsResultsLoading(false);
      }
    };

    if (searchQuery) {
      getSuggestions(searchQuery);
    } else {
      setSearchResults(null);
      setIsResultsLoading(false);
    }
  }, [searchQuery, announce, announceLoading]);

  if (isResultsLoading) {
    return (
      <div role="status" aria-live="polite">
        <h2 className={styles.resultsForLabel}>Sniffing out results for &ldquo;{searchQuery}&rdquo;&hellip;</h2>
        <div className={styles.waitingAnimationContainer}>
          <Lottie aria-hidden="true" className={styles.animationComponent} animationData={animation} loop={true} />
        </div>
      </div>
    );
  }

  return (
    <section aria-label="Search Results" aria-live="polite">
      <h2 className={isNavError ? styles.resultsForLabelError : styles.resultsForLabel} id="search-results-heading">
        {isNavError
          ? `We’re having trouble fetching this page right now`
          : isEmpty(searchResults)
            ? `We couldn’t sniff out results for “${searchQuery}”`
            : `Results for “${searchQuery}”`}
      </h2>

      {isNavError && <div className={styles.errorMessage}>{`Page error 404`}</div>}

      {(isEmpty(searchResults) || isNavError) && (
        <ContactUsButtonPanel title="If you need to contact us, we’re here 24/7" compact={true} />
      )}

      <ul className={styles.searchResultsList} role="list" aria-labelledby="search-results-heading">
        {map(searchResults, (dataItem: any, index: number) => (
          <li key={dataItem?.id} className={styles.searchResultItem}>
            <a
              href={generateSectionUrl(dataItem?.id)}
              onClick={e => {
                // Allow Ctrl+click (or Cmd+click) to open in new tab
                if (e.ctrlKey || e.metaKey) {
                  // Track analytics for new tab opens
                  track(`Search Results Clicked`, {
                    id: 'search-results-clicked-id',
                    parentId: 'search-results-id',
                    level: 'help-center',
                    type: 'section',
                    position: 1,
                    event_name: 'Link Clicked',
                    eventAction: 'click',
                    eventCategory: 'help-center-search-results',
                    eventLabel: 'help-center-search-results-click-new-tab',
                    name: 'Search Results Link Clicked (New Tab)',
                    searchValue: searchQuery,
                    sectionId: dataItem?.id,
                    sectionTitle: dataItem?.title || 'No title available.'
                  });
                  // Let the default behavior happen (open in new tab)
                  return;
                }
                e.preventDefault();
                doOnTopicClick(dataItem);
              }}
              className={styles.searchEntryContainer}
              aria-describedby={`search-result-${index}-description`}
            >
              {dataItem.title}
            </a>

            <p className={styles.searchEntryDescr} data-testid="search-entry-descr" id={`search-result-${index}-description`}>
              {dataItem?.snippet || 'No description available.'}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SearchResultsContainer;
